import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot, CheckCircle, Plus,
  ChatCircle, Table, DownloadSimple, UploadSimple,
  XCircle, Warning, Spinner, FileXls,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import ConfirmReviewPopup from "@/components/erp/ConfirmReviewPopup";

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]}
        components={{
          p:      ({ node, ...p }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold text-gray-900" {...p} />,
          ul:     ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...p} />,
          ol:     ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...p} />,
          li:     ({ node, ...p }) => <li className="text-sm" {...p} />,
          table:  ({ node, ...p }) => <table className="text-xs border-collapse my-1 w-full" {...p} />,
          th:     ({ node, ...p }) => <th className="border border-gray-300 px-2 py-0.5 bg-gray-100" {...p} />,
          td:     ({ node, ...p }) => <td className="border border-gray-300 px-2 py-0.5" {...p} />,
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ── Generic searchable dropdown ────────────────────────────────────────────────
function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options   = fieldUi.options || [];
  const ql        = q.toLowerCase();
  // Search runs over EVERY loaded option (label and code), not just the rows on screen
  const filtered  = q ? options.filter((o) => `${o.label || ""} ${o.value || ""}`.toLowerCase().includes(ql)) : options;
  const SHOW = 200;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${options.length ? options.length + " " : ""}${(fieldUi.label || fieldUi.field || "").replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-48 overflow-y-auto">
        {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No options available.</div>}
        {options.length > 0 && filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches for "{q}"</div>}
        {filtered.slice(0, SHOW).map((opt, i) => (
          <div key={(opt.value || "") + i} onClick={() => onPick(opt)}
            className="px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            {opt.label}
          </div>
        ))}
      </div>
      {filtered.length > SHOW && (
        <div className="px-3 py-1 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
          Showing {SHOW} of {filtered.length} — type to search all of them
        </div>
      )}
    </div>
  );
}

// ── Legal employer widget — reads from a ref so it always sees the latest data
// even when the parent chat bubble doesn't re-render (isLastAssistant pinned).
// getOptions is a stable getter () => string[] that always returns current value.
function LegalEmployerWidget({ getOptions, onRefresh, onPick }) {
  const [opts,    setOpts]    = React.useState(() => getOptions());
  const [timedOut, setTimedOut] = React.useState(false);
  React.useEffect(() => {
    if (opts.length > 0) return;
    let attempts = 0;
    const id = setInterval(() => {
      const current = getOptions();
      if (current.length > 0) { setOpts(current); clearInterval(id); return; }
      attempts++;
      if (attempts >= 30) { // 30 × 300ms = 9s timeout
        clearInterval(id);
        setTimedOut(true);
      }
    }, 300);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (opts.length === 0) {
    return timedOut ? (
      <div className="mt-2 px-3 py-2.5 text-xs text-gray-400 border border-gray-200 rounded-lg flex items-center gap-2">
        <span>Could not load employers from Oracle.</span>
        <button onClick={() => { setTimedOut(false); onRefresh?.(); }}
          className="text-[#0020C2] underline hover:no-underline">Retry</button>
      </div>
    ) : (
      <div className="mt-2 px-3 py-2.5 text-xs text-gray-400 border border-gray-200 rounded-lg flex items-center gap-1.5">
        <Spinner size={11} className="animate-spin text-[#0020C2]" /> Loading legal employers from Oracle…
      </div>
    );
  }
  return <SearchableChatOptions fieldUi={{ field: "legal_employer_name", label: "Legal Employer", options: opts }} onPick={onPick} />;
}

// ── Manager widget — polls ref until options arrive, then shows dropdown ──────
function ManagerWidget({ getOptions, onPick }) {
  const [opts, setOpts] = React.useState(() => getOptions());
  React.useEffect(() => {
    if (opts.length > 0) return;
    const id = setInterval(() => {
      const current = getOptions();
      if (current.length > 0) { setOpts(current); clearInterval(id); }
    }, 300);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (getOptions().length > 0) setOpts(getOptions()); }, [getOptions]);

  if (opts.length === 0) {
    return (
      <div className="mt-2 px-3 py-2 text-xs text-gray-400 border border-gray-200 rounded-lg flex items-center gap-1.5">
        <Spinner size={11} className="animate-spin text-[#0020C2]" /> Loading managers from Oracle…
      </div>
    );
  }
  return (
    <SearchableChatOptions
      fieldUi={{ field: "manager_assignment_id", label: "Manager", options: opts }}
      onPick={onPick} />
  );
}

// ── Phone type widget — polls ref until Oracle LOV arrives ────────────────────
function PhoneTypeWidget({ getOptions, onPick }) {
  const [opts, setOpts] = React.useState(() => getOptions());
  React.useEffect(() => {
    if (opts.length > 0) return;
    const id = setInterval(() => {
      const cur = getOptions();
      if (cur.length > 0) { setOpts(cur); clearInterval(id); }
    }, 300);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { const cur = getOptions(); if (cur.length > 0) setOpts(cur); }, [getOptions]);

  if (opts.length === 0) {
    return (
      <div className="mt-2 px-3 py-2 text-xs text-gray-400 border border-gray-200 rounded-lg flex items-center gap-1.5">
        <Spinner size={11} className="animate-spin text-[#0020C2]" /> Loading phone types from Oracle…
      </div>
    );
  }
  return (
    <SearchableChatOptions
      fieldUi={{ field: "phone_type", label: "Phone Type", options: opts }}
      onPick={onPick} />
  );
}

// ── Position search dropdown — receives results + search fn from parent ───────
// State lives in the parent component so re-renders don't cause duplicate API calls.
function PositionSearchDropdown({ results, loading, onSearch, onPick, onSkip }) {
  const [q, setQ]       = React.useState("");
  const timerRef        = React.useRef(null);
  const initialFiredRef = React.useRef(false);

  // Fire an initial empty search when the dropdown first appears
  React.useEffect(() => {
    if (initialFiredRef.current) return;
    initialFiredRef.current = true;
    onSearch("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const v = e.target.value;
    setQ(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearch(v), 350);
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
         style={{ minWidth: "min(680px, 90vw)" }}>
      <div className="flex items-center border-b border-gray-100">
        <input autoFocus value={q} onChange={handleChange}
          placeholder="Search by name, job or department…"
          className="flex-1 px-3 py-2 text-xs outline-none bg-white" />
        {loading && <Spinner size={12} className="animate-spin text-[#0020C2] mr-2 shrink-0" />}
      </div>
      <div className="grid px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-semibold text-gray-400 uppercase tracking-wide gap-2"
           style={{ gridTemplateColumns: "2.5fr 0.8fr 1.5fr 2.2fr" }}>
        <span>Name</span><span>Code</span><span>Job</span><span>Department</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {!loading && results.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            {q ? `No positions matching "${q}"` : "Type to search positions…"}
          </div>
        )}
        {results.map((opt, i) => (
          <div key={(opt.value || "") + i}
            onClick={() => onPick({ value: opt.value, label: opt.label,
              _dept: opt.department || "", _bu: opt.bu || "",
              _job_code: opt.job_code || "", _job_name: opt.job_name || "",
              position_id: opt.position_id || "", dept_id: opt.dept_id || "" })}
            className="grid px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0 items-center gap-2"
            style={{ gridTemplateColumns: "2.5fr 0.8fr 1.5fr 2.2fr" }}>
            <span className="font-medium text-gray-800 truncate" title={(opt.label || "").split(" | ")[0]}>
              {(opt.label || "").split(" | ")[0]}</span>
            <span className="text-gray-500 font-mono text-[11px] truncate">{opt.value}</span>
            <span className="text-gray-600 truncate">{opt.job_name || "—"}</span>
            <span className="text-gray-500 truncate">{opt.department || "—"}</span>
          </div>
        ))}
      </div>
      {results.length >= 500 && (
        <div className="px-3 py-1 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
          Showing first 500 — type to narrow results
        </div>
      )}
      {onSkip && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50">
          <button onClick={onSkip} className="text-[11px] text-gray-400 hover:text-[#0020C2]">
            Skip — position is optional
          </button>
        </div>
      )}
    </div>
  );
}

// ── Position dropdown — 4-column table ────────────────────────────────────────
function PositionDropdown({ options, onPick }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? options.filter((o) =>
        (o.label || "").toLowerCase().includes(q.toLowerCase()) ||
        (o.department || "").toLowerCase().includes(q.toLowerCase()) ||
        (o.job_name || "").toLowerCase().includes(q.toLowerCase()))
    : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
         style={{ minWidth: "min(680px, 90vw)" }}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name, job or department…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="grid px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-semibold text-gray-400 uppercase tracking-wide gap-2"
           style={{ gridTemplateColumns: "2.5fr 0.8fr 1.5fr 2.2fr" }}>
        <span>Name</span><span>Code</span><span>Job</span><span>Department</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No positions — position is optional.</div>}
        {filtered.slice(0, 100).map((opt, i) => (
          <div key={(opt.value || "") + i}
            onClick={() => onPick({ value: opt.value, label: opt.label,
              _dept: opt.department || "", _bu: opt.bu || "",
              _job_code: opt.job_code || "", _job_name: opt.job_name || "",
              position_id: opt.position_id || "", dept_id: opt.dept_id || "" })}
            className="grid px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0 items-center gap-2"
            style={{ gridTemplateColumns: "2.5fr 0.8fr 1.5fr 2.2fr" }}>
            <span className="font-medium text-gray-800 truncate" title={(opt.label || "").split(" | ")[0]}>
              {(opt.label || "").split(" | ")[0]}</span>
            <span className="text-gray-500 font-mono text-[11px] truncate">{opt.value}</span>
            <span className="text-gray-600 truncate">{opt.job_name || "—"}</span>
            <span className="text-gray-500 truncate">{opt.department || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Radio options ──────────────────────────────────────────────────────────────
function RadioOptions({ fieldUi, onPick }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {(fieldUi.options || []).map((opt) => (
        <button key={opt.value} onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors">
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Date picker ────────────────────────────────────────────────────────────────
function DatePickerWidget({ fieldUi, onPick }) {
  const [val, setVal] = React.useState("");
  const label = fieldUi?.label || (fieldUi?.field || "").replace(/_/g, " ");
  return (
    <div className="mt-2 border border-[#0020C2]/30 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-[#0020C2]/5 px-3 py-2 text-xs font-medium text-[#0020C2] border-b border-[#0020C2]/10">
        📅 Select {label}
      </div>
      <div className="px-3 py-2.5 flex items-center gap-2">
        <input type="date" value={val} onChange={(e) => setVal(e.target.value)}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#0020C2] bg-white" />
        <button onClick={() => val && onPick(val)} disabled={!val}
          className="text-xs px-4 py-2 bg-[#0020C2] text-white rounded-lg disabled:opacity-40 font-medium hover:bg-[#0018A0]">
          Confirm
        </button>
      </div>
    </div>
  );
}

// ── Chat bubble ────────────────────────────────────────────────────────────────
function HRChatBubble({
  msg, isLastAssistant,
  getLegalEmployers, onRefreshEmployers, legalEmployers, businessUnits, buLoading, buError, allBUs,
  genderOptions, maritalOptions, phoneTypeOptions, getPhoneTypeOptions,
  posSearchResults, posSearchLoading, onSearchPositions,
  departmentOptions, allDepartments, jobOptions, filteredJobOptions,
  getManagerOptions, managerOptions,
  onOptionClick,
}) {
  const isUser  = msg.role === "user";
  const fieldUi = !isUser && !msg.loading && isLastAssistant ? (msg.result?.next_field_ui ?? null) : null;
  const uiType  = fieldUi?.uiType;

  const handlePick = (opt) => onOptionClick?.(opt, fieldUi?.field);

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
        isUser ? "bg-[#0020C2] text-white rounded-tr-sm" : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"
      }`}>
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0,150,300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay:`${d}ms` }} />)}
          </div>
        )}
        {!msg.loading && msg.text && (
          isUser ? <p className="whitespace-pre-wrap">{msg.text}</p> : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {/* Legal Employer */}
        {uiType === "searchable_dropdown" && fieldUi.field === "legal_employer_name" && (
          <LegalEmployerWidget getOptions={getLegalEmployers} onRefresh={onRefreshEmployers} onPick={handlePick} />
        )}

        {/* Business Unit */}
        {uiType === "searchable_dropdown" && fieldUi.field === "business_unit_name" && (
          <SearchableChatOptions
            fieldUi={{ ...fieldUi, options: (allBUs?.length ? allBUs : businessUnits).map((b) => ({ label: b.label, value: b.value, id: b.id })) }}
            onPick={(opt) => onOptionClick?.(opt, "business_unit_name")} />
        )}

        {/* Country Code */}
        {uiType === "searchable_dropdown" && fieldUi.field === "country_code_number" && (
          <SearchableChatOptions fieldUi={fieldUi}
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "country_code_number")} />
        )}

        {/* Position — search-as-you-type, state lives in parent, no duplicate calls */}
        {uiType === "searchable_dropdown" && fieldUi.field === "position_code" && (
          <PositionSearchDropdown
            results={posSearchResults || []}
            loading={posSearchLoading}
            onSearch={onSearchPositions}
            onPick={(opt) => onOptionClick?.(opt, "position_code")}
            onSkip={() => onOptionClick?.({ value: "__skip__", label: "Skip" }, "position_code")} />
        )}

        {/* Department */}
        {uiType === "searchable_dropdown" && fieldUi.field === "department_name" && (
          <SearchableChatOptions
            fieldUi={{ ...fieldUi, options: departmentOptions?.length ? departmentOptions : (allDepartments || []) }}
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "department_name")} />
        )}

        {/* Job */}
        {uiType === "searchable_dropdown" && fieldUi.field === "job_code" && (
          <SearchableChatOptions
            fieldUi={{ ...fieldUi, options: filteredJobOptions?.length > 0 ? filteredJobOptions : (jobOptions || []) }}
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "job_code")} />
        )}

        {/* Manager — polls ref so it appears as soon as /managers fetch completes */}
        {uiType === "searchable_dropdown" && fieldUi.field === "manager_assignment_id" && (
          <ManagerWidget
            getOptions={getManagerOptions}
            onPick={(opt) => {
              const payload = `${opt.value}|${opt.assignment_id || ""}|${opt.manager_type || "LINE_MANAGER"}`;
              onOptionClick?.({ value: payload, label: opt.label }, "manager_assignment_id");
            }} />
        )}

        {/* Phone Type — polls ref until Oracle LOV arrives */}
        {uiType === "searchable_dropdown" && fieldUi.field === "phone_type" && (
          <PhoneTypeWidget getOptions={getPhoneTypeOptions}
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "phone_type")} />
        )}

        {/* Date picker */}
        {uiType === "date_picker" && (
          <DatePickerWidget fieldUi={fieldUi} onPick={(dateStr) => {
            onOptionClick?.({ value: `${fieldUi.field}: ${dateStr}`, label: dateStr, _display: dateStr }, fieldUi.field);
          }} />
        )}

        {/* Radio */}
        {uiType === "radio" && (() => {
          const liveOpts = fieldUi.field === "gender"         ? (genderOptions?.length  ? genderOptions  : fieldUi.options)
                         : fieldUi.field === "marital_status" ? (maritalOptions?.length ? maritalOptions : fieldUi.options)
                         : fieldUi.options;
          return liveOpts?.length > 0
            ? <RadioOptions fieldUi={{ ...fieldUi, options: liveOpts }} onPick={(opt) => onOptionClick?.(opt, fieldUi.field)} />
            : null;
        })()}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>{msg.timestamp}</div>
        )}
      </div>
    </div>
  );
}

// ── Input bar ──────────────────────────────────────────────────────────────────
function HRInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50 focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea ref={inputRef} value={value} onChange={onChange} onKeyDown={onKeyDown}
          rows={1} disabled={busy} placeholder="Continue the conversation…"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }} />
        <button onClick={onSend} disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ── Wizard panel ───────────────────────────────────────────────────────────────
const WIZARD_FIELDS = [
  { key: "first_name",          label: "First Name",      required: true  },
  { key: "last_name",           label: "Last Name",       required: true  },
  { key: "legal_employer_name", label: "Legal Employer",  required: true  },
  { key: "legislation_code",    label: "Legislation Code",required: false },
  { key: "business_unit_name",  label: "Business Unit",   required: false },
  { key: "start_date",          label: "Start Date",      required: true  },
  { key: "date_of_birth",       label: "Date of Birth",   required: true  },
  { key: "gender",              label: "Gender",          required: true  },
  { key: "marital_status",      label: "Marital Status",  required: true  },
  { key: "email",               label: "Work Email",      required: true  },
  { key: "country_code_number", label: "Country Code",    required: false },
  { key: "phone_type",          label: "Phone Type",      required: true  },
  { key: "phone_number",        label: "Phone",           required: true  },
  { key: "address_line1",       label: "Address",         required: true  },
  { key: "city",                label: "City",            required: true  },
  { key: "position_code",       label: "Position",        required: false },
  { key: "department_name",     label: "Department",      required: false },
  { key: "job_code",            label: "Job",             required: false },
];

function WizardPanel({ fieldValues, workerCreated, oracleResult, onChangeEmployer }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
        <Sparkle size={11} weight="fill" /> Worker Details
        <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">· auto-filled as you chat</span>
      </div>
      {WIZARD_FIELDS.map(({ key, label, required }) => {
        const value  = fieldValues[key];
        const filled = Boolean(value) && value !== "__skip__";
        return (
          <div key={key} className="space-y-0.5">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
              {label}{required && <span className="text-red-400">*</span>}
              {filled && <CheckCircle size={9} weight="fill" className="text-green-500 ml-auto" />}
            </div>
            <div className={`text-sm px-3 py-2 rounded border ${
              filled ? "border-[#0020C2]/20 bg-[#0020C2]/5 text-gray-900 font-medium" : "border-gray-100 bg-gray-50 text-gray-400 italic"
            }`}>
              {filled ? value : "Not yet provided"}
            </div>
            {key === "legal_employer_name" && filled && onChangeEmployer && !workerCreated && (
              <button onClick={onChangeEmployer} className="text-[10px] text-[#0020C2] hover:underline">
                Change Legal Employer
              </button>
            )}
          </div>
        );
      })}
      {workerCreated && oracleResult && (
        <div className="mt-2 p-3 bg-green-50 rounded-xl border border-green-100">
          <div className="flex items-center gap-1.5 text-green-700 font-bold text-sm mb-1">
            <CheckCircle size={14} weight="fill" /> Worker Created
          </div>
          {oracleResult.PersonNumber && (
            <div className="text-xs text-green-600">Person #: <strong>{oracleResult.PersonNumber}</strong></div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Bulk upload components ─────────────────────────────────────────────────────
function BulkStatusBadge({ valid }) {
  return valid
    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full"><CheckCircle size={10} weight="fill" /> Valid</span>
    : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full"><XCircle size={10} weight="fill" /> Errors</span>;
}

function BulkUploadZone({ onFileSelected, onDownloadTemplate, downloading }) {
  const inputRef = React.useRef(null);
  const [drag, setDrag] = React.useState(false);
  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) { alert("Only .xlsx files are accepted."); return; }
    onFileSelected(file);
  };
  return (
    <div className="flex flex-col items-center gap-6 py-10 px-8">
      <div className="w-full max-w-md bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <FileXls size={28} weight="duotone" className="text-[#0020C2] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Step 1 — Download Template</p>
          <p className="text-xs text-gray-500 mt-0.5">Excel template with Oracle LOV dropdowns pre-filled.</p>
        </div>
        <button onClick={onDownloadTemplate} disabled={downloading}
          className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-50 px-3 py-2 rounded-lg">
          {downloading ? <Spinner size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />}
          Download
        </button>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`w-full max-w-md border-2 border-dashed rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
          drag ? "border-[#0020C2] bg-blue-50" : "border-gray-300 hover:border-[#0020C2] hover:bg-gray-50"}`}>
        <UploadSimple size={36} weight="duotone" className="text-gray-400" />
        <p className="text-sm font-semibold text-gray-700">Step 2 — Drop your filled Excel here</p>
        <p className="text-xs text-gray-400">or click to browse (.xlsx)</p>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>
    </div>
  );
}

function BulkPreviewTable({ rows }) {
  const cols = [
    { key: "legal_employer_name", label: "Legal Employer" },
    { key: "first_name",          label: "First Name" },
    { key: "last_name",           label: "Last Name" },
    { key: "email",               label: "Email" },
    { key: "start_date",          label: "Start Date" },
  ];
  return (
    <div className="overflow-auto border border-gray-200 rounded-xl h-full">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-12">Row</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-20">Status</th>
            {cols.map((c) => <th key={c.key} className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 whitespace-nowrap">{c.label}</th>)}
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Errors / Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={row.valid ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
              <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{i + 1}</td>
              <td className="px-3 py-2 border-b border-gray-100"><BulkStatusBadge valid={row.valid} /></td>
              {cols.map((c) => <td key={c.key} className="px-3 py-2 border-b border-gray-100 text-gray-700 max-w-[140px] truncate">{row[c.key] || "—"}</td>)}
              <td className="px-3 py-2 border-b border-gray-100 text-[11px]">
                {(row.errors || []).length > 0 && <div className="text-red-600">{row.errors.join("; ")}</div>}
                {(row.notes || []).length > 0 && <div className="text-amber-700">{row.notes.join("; ")}</div>}
                {!(row.errors || []).length && !(row.notes || []).length && "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
const SESSION_KEY = "hr_worker_session_id";
const BULK_STAGE  = { UPLOAD: "UPLOAD", VALIDATING: "VALIDATING", PREVIEW: "PREVIEW", SUMMARY: "SUMMARY" };

export default function HRWorkerAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile } = useErp();

  // ── Tab state ────────────────────────────────────────────────────────────────
  const [tab, setTab] = React.useState("chat");

  // ── Chat state ───────────────────────────────────────────────────────────────
  const [sessionId,     setSessionId]     = React.useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [messages,      setMessages]      = React.useState([]);
  const [input,         setInput]         = React.useState("");
  const [busy,          setBusy]          = React.useState(false);
  const [chatStarted,   setChatStarted]   = React.useState(false);
  const startedRef = React.useRef(false); // ref guard — synchronous, immune to StrictMode double-invoke
  const [fieldValues,   setFieldValues]   = React.useState({});
  const [workerCreated, setWorkerCreated] = React.useState(false);
  const [oracleResult,  setOracleResult]  = React.useState(null);
  const [showConfirm,   setShowConfirm]   = React.useState(false);

  // ── LOV state ────────────────────────────────────────────────────────────────
  const [legalEmployers,    setLegalEmployers]    = React.useState([]);
  const [businessUnits,     setBusinessUnits]     = React.useState([]);
  const [allBUs,            setAllBUs]            = React.useState([]);
  const [genderOptions,     setGenderOptions]     = React.useState([]);
  const [maritalOptions,    setMaritalOptions]    = React.useState([]);
  const [buLoading,         setBuLoading]         = React.useState(false);
  const [buError,           setBuError]           = React.useState(null);
  const [selectedBuId,       setSelectedBuId]      = React.useState(null);
  const [departmentOptions,  setDepartmentOptions] = React.useState([]);
  const [posSearchResults,   setPosSearchResults]  = React.useState([]);
  const [posSearchLoading,   setPosSearchLoading]  = React.useState(false);
  const posSearchTimerRef    = React.useRef(null);
  const posSearchSeqRef      = React.useRef(0);
  const selectedBuRef        = React.useRef({ id: "", name: "" }); // BU filter for position search     // sequence counter — ignores stale responses
  const [jobOptions,        setJobOptions]        = React.useState([]);
  const [allDepartments,    setAllDepartments]    = React.useState([]); // fallback when no position picked
  const [filteredJobOptions,setFilteredJobOptions]= React.useState([]);
  const [managerOptions,    setManagerOptions]    = React.useState([]);
  const [phoneTypeOptions,  setPhoneTypeOptions]  = React.useState([]);

  // ── Bulk state ───────────────────────────────────────────────────────────────
  const [bulkStage,   setBulkStage]   = React.useState(BULK_STAGE.UPLOAD);
  const [bulkFile,    setBulkFile]    = React.useState(null);
  const [bulkParsed,  setBulkParsed]  = React.useState(null);
  const [bulkResults, setBulkResults] = React.useState(null);
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [bulkError,   setBulkError]   = React.useState("");

  const messagesEndRef      = React.useRef(null);
  const inputRef            = React.useRef(null);
  const legalEmployersRef   = React.useRef([]);
  const managerOptionsRef   = React.useRef([]);
  const phoneTypeOptionsRef = React.useRef([]);
  const getLegalEmployers   = React.useCallback(() => legalEmployersRef.current,   []);
  const getManagerOptions   = React.useCallback(() => managerOptionsRef.current,   []);
  const getPhoneTypeOptions = React.useCallback(() => phoneTypeOptionsRef.current, []);

  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── Init session + pre-fetch LOVs ────────────────────────────────────────────
  React.useEffect(() => {
    if (openAgent !== "create-hr" || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false; // declared outside async IIFE so cleanup can access it
    (async () => {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      setSessionId(existing);
    } else {
      // Await /start so sessionId is set before the user can send
      try {
        const { data } = await api.post("/erp-agents/hr-worker-create/start");
        sessionStorage.setItem(SESSION_KEY, data.session_id);
        setSessionId(data.session_id);
      } catch (e) { console.error("[HR] /start failed:", e); }
    }

    api.get("/erp-agents/hr-worker-create/legal-employers")
      .then(({ data }) => {
        const emps = data.employers || [];
        // Always update the ref — it's mutable and not React state,
        // so the LegalEmployerWidget's setInterval can read it regardless of
        // whether cancelled was set by a StrictMode unmount/remount cycle.
        legalEmployersRef.current = emps;
        if (!cancelled) setLegalEmployers(emps);
      }).catch(() => {});
    api.get("/erp-agents/hr-worker-create/business-units")
      .then(({ data }) => { if (!cancelled) setAllBUs(data.business_units || []); }).catch(() => {});
    // Positions fetch is deferred — triggered lazily when the position step is reached.
    // This avoids blocking modal open on a 7800+ position paginated fetch (~20s).
    api.get("/erp-agents/hr-worker-create/jobs")
      .then(({ data }) => { if (!cancelled) setJobOptions(data.options || []); }).catch(() => {});
    api.get("/erp-agents/hr-worker-create/departments")
      .then(({ data }) => { if (!cancelled) setAllDepartments(data.options || []); }).catch(() => {});
    api.get("/erp-agents/hr-worker-create/managers")
      .then(({ data }) => {
        const opts = data.options || [];
        managerOptionsRef.current = opts;
        if (!cancelled) setManagerOptions(opts);
      }).catch(() => {});
    api.get("/erp-agents/hr-worker-create/phone-types")
      .then(({ data }) => {
        const opts = data.options || [];
        phoneTypeOptionsRef.current = opts;   // always update ref — widget polls this
        if (!cancelled) setPhoneTypeOptions(opts);
      }).catch(() => {});
    })(); // end async IIFE
    return () => { cancelled = true; };
  }, [openAgent]); // startedRef is a ref — not needed as dependency

  const close = () => { sessionStorage.removeItem(SESSION_KEY); setOpenAgent(null); onClose?.(); };

  const startNewChat = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId(""); setMessages([]); setInput(""); startedRef.current = false;
    setChatStarted(false); setFieldValues({}); setWorkerCreated(false);
    setOracleResult(null); setShowConfirm(false);
    setBusinessUnits([]); setGenderOptions([]); setMaritalOptions([]);
    setSelectedBuId(null); setDepartmentOptions([]); setFilteredJobOptions([]);
  };

  // ── Fetch managers filtered by BU — called after BU or position is selected ──
  const fetchManagersForBU = React.useCallback((buId, buName) => {
    const params = buId   ? { business_unit_id:   buId   }
                 : buName ? { business_unit_name:  buName }
                 : {};
    api.get("/erp-agents/hr-worker-create/managers", { params })
      .then(({ data }) => {
        const opts = data.options || [];
        // Fall back to unfiltered list if Oracle returns empty for this BU
        if (opts.length > 0) {
          managerOptionsRef.current = opts;
          setManagerOptions(opts);
        }
      }).catch(() => {});
  }, []);

  // ── Re-fetch legal employers (called by Retry button) ────────────────────────
  const refreshEmployers = React.useCallback(() => {
    api.get("/erp-agents/hr-worker-create/legal-employers")
      .then(({ data }) => {
        const emps = data.employers || [];
        legalEmployersRef.current = emps;
        if (emps.length) setLegalEmployers(emps);
      }).catch(() => {});
  }, []);

  // ── Departments + jobs scoped to the selected legal employer / BU ───────────
  const loadEmployerLovs = React.useCallback((legalEmployer, businessUnit) => {
    const params = { legal_employer: legalEmployer || "", business_unit: businessUnit || "" };
    setAllDepartments([]); setJobOptions([]);
    api.get("/erp-agents/hr-worker-create/departments", { params })
      .then(({ data }) => setAllDepartments(data.options || [])).catch(() => {});
    api.get("/erp-agents/hr-worker-create/jobs", { params })
      .then(({ data }) => setJobOptions(data.options || [])).catch(() => {});
  }, []);

  // ── Position search — single call, parent owns results ───────────────────────
  const searchPositions = React.useCallback((q) => {
    // Sequence counter: each call gets a unique seq; only the latest response wins.
    const seq = ++posSearchSeqRef.current;
    setPosSearchLoading(true);
    api.get("/erp-agents/hr-worker-create/positions/search", { params: {
      q: q || "", limit: 500,
      bu_id:   selectedBuRef.current.id   || "",
      bu_name: selectedBuRef.current.name || "",
    } })
      .then(({ data }) => { if (seq === posSearchSeqRef.current) setPosSearchResults(data.options || []); })
      .catch(() => { if (seq === posSearchSeqRef.current) setPosSearchResults([]); })
      .finally(() => { if (seq === posSearchSeqRef.current) setPosSearchLoading(false); });
  }, []);

  // ── Send message ─────────────────────────────────────────────────────────────
  const sendMessage = React.useCallback(async (displayText, backendMsg) => {
    const userText   = (displayText || "").trim();
    const serverText = (backendMsg  || displayText || "").trim();
    if (!userText || busy || !sessionId) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const INTERNAL = ["__confirm__", "__edit__", "__change_employer__"];
    const isInternal = INTERNAL.includes(serverText) || serverText.startsWith("__bu_override__:");

    setMessages((prev) => [
      ...prev,
      ...(isInternal ? [] : [{ id: Date.now(), role: "user", text: userText, timestamp: ts() }]),
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);
    try {
      const { data } = await api.post("/erp-agents/hr-worker-create/chat", {
        session_id: sessionId, message: serverText,
      });
      if (data.fields_collected) setFieldValues((prev) => ({ ...prev, ...data.fields_collected }));
      if (data.worker_created) {
        setWorkerCreated(true); setOracleResult(data.oracle_response);
        setShowConfirm(false);
      } else if (data.ready_to_confirm) {
        setShowConfirm(true);
      } else {
        setShowConfirm(false);
      }
      const reply = data.reply || "";
      const now   = ts();
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, text: reply, result: data, timestamp: now } : m));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "An error occurred.";
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, text: `❌ ${detail}`, timestamp: ts() } : m));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [busy, sessionId, chatStarted]);

  // ── Option click handler ─────────────────────────────────────────────────────
  const handleOptionClick = React.useCallback((opt, fieldName) => {
    if (fieldName === "legal_employer_name") {
      const name    = opt.name || opt.value;
      const legCode = opt.legislation_code || "";
      setFieldValues((prev) => ({ ...prev, legal_employer_name: name, legislation_code: legCode || prev.legislation_code || "" }));
      setBusinessUnits([]); setBuLoading(true); setBuError(null);
      setSelectedBuId(null); setDepartmentOptions([]); setFilteredJobOptions([]);
      setPosSearchResults([]); // reset position search results for new employer
      loadEmployerLovs(name, "");   // departments/jobs for THIS employer only
      const legParam = legCode ? `?legislation_code=${encodeURIComponent(legCode)}` : "";
      api.get(`/erp-agents/hr-worker-create/gender-options${legParam}`)
        .then(({ data }) => setGenderOptions(data.options || [])).catch(() => {});
      api.get(`/erp-agents/hr-worker-create/marital-status-options${legParam}`)
        .then(({ data }) => setMaritalOptions(data.options || [])).catch(() => {});
      api.get("/erp-agents/hr-worker-create/business-units")
        .then(({ data }) => { setBusinessUnits(data.business_units || []); setBuError(data.error || null); })
        .catch(() => setBuError("Failed to load Business Units."))
        .finally(() => setBuLoading(false));
      sendMessage(name, [`legal_employer_name: ${name}`, legCode ? `legislation_code: ${legCode}` : ""].filter(Boolean).join(", "));

    } else if (fieldName === "business_unit_name") {
      const buId   = opt.id || null;
      const buName = opt.value || opt.label;
      setSelectedBuId(buId);
      selectedBuRef.current = { id: buId ? String(buId) : "", name: buName || "" };
      setPosSearchResults([]); // drop results from any previous BU
      setDepartmentOptions([]);
      setFilteredJobOptions([]);
      // Re-fetch managers filtered to this BU so the manager dropdown
      // only shows people who work in the same business unit
      fetchManagersForBU(buId, buName);
      loadEmployerLovs(fieldValues.legal_employer_name || "", buName);
      sendMessage(buName, buName);

    } else if (fieldName === "position_code") {
      if (opt._dept) setDepartmentOptions([{ label: opt._dept, value: opt._dept }]);
      if (opt._job_code && opt._job_name) setFilteredJobOptions([{ label: opt._job_name, value: opt._job_code }]);
      else setFilteredJobOptions([]);
      if (opt._bu) fetchManagersForBU(null, opt._bu);
      const display = (opt.label || "").split(" | ")[0] || opt.value;
      // Build inline key:value pairs on ONE line — no newlines, no __bu_override__ second message
      const parts = [`position_code: ${opt.value}`];
      if (opt.position_id) parts.push(`position_id: ${opt.position_id}`);
      if (opt.dept_id)     parts.push(`dept_id: ${opt.dept_id}`);
      sendMessage(display, parts.join(", "));

    } else {
      const display = opt._display || opt.label || opt.value;
      sendMessage(display, opt.value);
    }
  }, [sendMessage, loadEmployerLovs, fieldValues.legal_employer_name]);

  const handleKeyDown    = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };
  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  // ── Bulk handlers ─────────────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setBulkLoading(true); setBulkError("");
    try {
      const resp = await api.get("/erp-agents/hr-worker-create/bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "hr_worker_bulk_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setBulkError("Download failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBulkLoading(false); }
  };

  const handleFileSelected = async (file) => {
    setBulkFile(file); setBulkError(""); setBulkStage(BULK_STAGE.VALIDATING);
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post("/erp-agents/hr-worker-create/bulk/validate-file", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setBulkParsed(data); setBulkStage(BULK_STAGE.PREVIEW);
    } catch (e) {
      setBulkError(e?.response?.data?.detail || e.message || "Validation failed.");
      setBulkStage(BULK_STAGE.UPLOAD);
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkParsed) return;
    const validRows = bulkParsed.rows.filter((r) => r.valid);
    if (!validRows.length) return;
    setBulkLoading(true); setBulkError("");
    try {
      const { data } = await api.post("/erp-agents/hr-worker-create/bulk/submit", { rows: validRows });
      setBulkResults(data); setBulkStage(BULK_STAGE.SUMMARY);
    } catch (e) { setBulkError("Submission failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBulkLoading(false); }
  };

  const handleBulkReupload = () => { setBulkParsed(null); setBulkFile(null); setBulkError(""); setBulkStage(BULK_STAGE.UPLOAD); };

  // ── Last assistant message id (for fieldUi rendering) ────────────────────────
  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant" && !m.loading)?.id;

  if (openAgent !== "create-hr") return null;

  return (
    <>
      <style>{`
        @keyframes hrChatFadeIn   { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
        @keyframes hrWizardSlide  { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes hrFadeIn       { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .hr-modal-pre  { width:640px; max-width:calc(100vw - 2rem); max-height:90vh; animation:hrFadeIn 0.3s ease; }
        .hr-modal-chat { width:min(92vw,1400px); height:90vh; animation:hrFadeIn 0.3s ease; }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={close}>
        <div onClick={(e) => e.stopPropagation()}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden shadow-2xl ${chatStarted ? "hr-modal-chat" : "hr-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}>

          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle HCM · Vaku AI</div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">HR Worker Manager</h3>
              <p className="text-xs text-gray-500 mt-0.5">Create new worker records in Oracle HCM</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Chat / Bulk tabs */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button onClick={() => setTab("chat")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === "chat" ? "bg-white text-[#0020C2] shadow-sm font-semibold" : "text-gray-500 hover:text-gray-700"}`}>
                  <ChatCircle size={12} /> Chat
                </button>
                <button onClick={() => setTab("bulk")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === "bulk" ? "bg-white text-[#0020C2] shadow-sm font-semibold" : "text-gray-500 hover:text-gray-700"}`}>
                  <Table size={12} /> Bulk Upload
                </button>
              </div>
              {chatStarted && tab === "chat" && (
                <>
                  <button onClick={startNewChat}
                    className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0">
                    <Plus size={13} weight="bold" /> New Chat
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button onClick={() => { close(); setSetupOpen?.(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
          </div>

          {/* ── CHAT TAB ── */}
          {tab === "chat" && (
            <>
              {!chatStarted ? (
                /* Pre-chat: wizard + input */
                <>
                  <div className="flex-1 overflow-y-auto p-5">
                    <p className="text-sm text-gray-600 mb-4">
                      Start typing to create a new worker record. Fields fill in as you chat.
                    </p>
                    <WizardPanel fieldValues={fieldValues} workerCreated={workerCreated} oracleResult={oracleResult} />
                  </div>
                  {!sessionId && (
                    <div className="px-4 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
                      <Spinner size={12} className="animate-spin inline mr-1" /> Connecting to Oracle…
                    </div>
                  )}
                  <HRInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                    onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy || !sessionId} />
                </>
              ) : (
                /* Chat mode: 60% chat / 40% wizard */
                <div className="flex flex-1 overflow-hidden">
                  <div className="flex flex-col overflow-hidden border-r border-gray-200"
                    style={{ width: "60%", animation: "hrChatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                      {messages.map((msg) => (
                        <HRChatBubble key={msg.id} msg={msg}
                          isLastAssistant={msg.id === lastAssistantId}
                          getLegalEmployers={getLegalEmployers}
                          onRefreshEmployers={refreshEmployers}
                          legalEmployers={legalEmployers}
                          businessUnits={businessUnits} buLoading={buLoading} buError={buError} allBUs={allBUs}
                          genderOptions={genderOptions} maritalOptions={maritalOptions}
                          phoneTypeOptions={phoneTypeOptions} getPhoneTypeOptions={getPhoneTypeOptions}
                          posSearchResults={posSearchResults} posSearchLoading={posSearchLoading}
                          onSearchPositions={searchPositions}
                          departmentOptions={departmentOptions} allDepartments={allDepartments} jobOptions={jobOptions}
                          filteredJobOptions={filteredJobOptions}
                          getManagerOptions={getManagerOptions} managerOptions={managerOptions}
                          onOptionClick={handleOptionClick} />
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                    <HRInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                      onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy} />
                  </div>
                  <div className="flex flex-col bg-white overflow-y-auto"
                    style={{ width: "40%", minWidth: "360px", animation: "hrWizardSlide 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                    <div className="flex-1 p-4">
                      <WizardPanel fieldValues={fieldValues} workerCreated={workerCreated}
                        oracleResult={oracleResult}
                        onChangeEmployer={() => sendMessage("__change_employer__", "__change_employer__")} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── BULK TAB ── */}
          {tab === "bulk" && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {bulkStage === BULK_STAGE.UPLOAD && (
                <>
                  {bulkError && (
                    <div className="mx-5 mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 shrink-0">
                      <XCircle size={14} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{bulkError}</p>
                    </div>
                  )}
                  <BulkUploadZone onFileSelected={handleFileSelected} onDownloadTemplate={handleDownloadTemplate} downloading={bulkLoading} />
                </>
              )}
              {bulkStage === BULK_STAGE.VALIDATING && (
                <div className="flex flex-col items-center justify-center gap-4 py-16">
                  <Spinner size={36} className="animate-spin text-[#0020C2]" />
                  <p className="text-sm font-semibold text-gray-700">Validating {bulkFile?.name}…</p>
                  <p className="text-xs text-gray-400">Checking all rows against Oracle</p>
                </div>
              )}
              {bulkStage === BULK_STAGE.PREVIEW && bulkParsed && (
                <div className="flex flex-col gap-4 p-5 h-full overflow-hidden">
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex-1 text-sm">
                      <span className="font-bold text-gray-900">{bulkFile?.name}</span>
                      <span className="text-gray-500 ml-2">— {bulkParsed.total_rows} row{bulkParsed.total_rows !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <span className="text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">✓ {bulkParsed.valid_rows} valid</span>
                      {bulkParsed.invalid_rows > 0 && <span className="text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">✗ {bulkParsed.invalid_rows} invalid</span>}
                    </div>
                  </div>
                  {bulkError && <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 shrink-0"><XCircle size={14} weight="fill" className="text-red-500 shrink-0 mt-0.5" /><p className="text-xs text-red-700">{bulkError}</p></div>}
                  {bulkParsed.invalid_rows > 0 && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 shrink-0">
                      <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{bulkParsed.invalid_rows} row{bulkParsed.invalid_rows !== 1 ? "s" : ""} will be skipped.{bulkParsed.valid_rows > 0 ? ` Only the ${bulkParsed.valid_rows} valid row${bulkParsed.valid_rows !== 1 ? "s" : ""} will be submitted.` : " Fix errors and re-upload."}</p>
                    </div>
                  )}
                  <div className="flex-1 overflow-auto min-h-0"><BulkPreviewTable rows={bulkParsed.rows} /></div>
                  <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                    <button onClick={handleBulkReupload} disabled={bulkLoading} className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-1.5 disabled:opacity-50"><UploadSimple size={13} /> Upload different file</button>
                    <button onClick={handleBulkSubmit} disabled={!bulkParsed.valid_rows || bulkLoading}
                      className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-40 rounded-lg transition-colors">
                      {bulkLoading ? <><Spinner size={14} className="animate-spin" /> Submitting…</> : `Create ${bulkParsed.valid_rows} Worker${bulkParsed.valid_rows !== 1 ? "s" : ""} in Oracle`}
                    </button>
                  </div>
                </div>
              )}
              {bulkStage === BULK_STAGE.SUMMARY && bulkResults && (
                <div className="flex flex-col gap-5 p-6 h-full overflow-hidden">
                  <div className="flex flex-col items-center gap-3 py-4 shrink-0">
                    {bulkResults.failed === 0 ? <CheckCircle size={48} weight="fill" className="text-[#0020C2]" /> : <Warning size={48} weight="fill" className="text-amber-500" />}
                    <h2 className="text-xl font-black text-gray-900">{bulkResults.failed === 0 ? "Bulk Upload Complete!" : "Completed with Errors"}</h2>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-semibold text-green-700">{bulkResults.succeeded} succeeded</span>
                      {bulkResults.failed > 0 && <span className="font-semibold text-red-700">{bulkResults.failed} failed</span>}
                      <span className="text-gray-400">(of {bulkResults.submitted} submitted)</span>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto min-h-0 border border-gray-200 rounded-xl">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-14">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Result</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResults.results.map((r) => (
                          <tr key={r.row_num} className={r.success ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
                            <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{r.row_num}</td>
                            <td className="px-3 py-2 border-b border-gray-100">{r.success ? <span className="flex items-center gap-1 text-green-700 font-semibold"><CheckCircle size={12} weight="fill" /> Created</span> : <span className="flex items-center gap-1 text-red-700 font-semibold"><XCircle size={12} weight="fill" /> Failed</span>}</td>
                            <td className="px-3 py-2 border-b border-gray-100 text-gray-600">{r.success ? (r.detail || "Worker created successfully") : (r.error || "Oracle failure")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                    <button onClick={handleBulkReupload} className="text-xs font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1.5"><UploadSimple size={13} /> Upload another file</button>
                    <button onClick={close} className="px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] rounded-lg">Done</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirm popup */}
      {showConfirm && !workerCreated && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-4 px-4">
          <div className="w-full max-w-lg my-auto">
            <ConfirmReviewPopup
              title="Create HR Worker"
              subtitle="Review the details before creating the worker record in Oracle HCM."
              confirmLabel="Create Worker"
              cancelLabel="Review / Edit"
              busy={busy}
              onConfirm={() => { setShowConfirm(false); sendMessage("__confirm__", "__confirm__"); }}
              onCancel={() => { setShowConfirm(false); sendMessage("__edit__", "__edit__"); }}
              fields={[
                { label: "First Name",     value: fieldValues.first_name          || "—" },
                { label: "Last Name",      value: fieldValues.last_name           || "—" },
                { label: "Legal Employer", value: fieldValues.legal_employer_name || "—" },
                { label: "Business Unit",  value: fieldValues.business_unit_name  || "—" },
                { label: "Start Date",     value: fieldValues.start_date          || "—", highlight: true },
                { label: "Date of Birth",  value: fieldValues.date_of_birth       || "—" },
                { label: "Gender",         value: fieldValues.gender              || "—" },
                { label: "Marital Status", value: fieldValues.marital_status      || "—" },
                { label: "Email",          value: fieldValues.email               || "—" },
                { label: "Phone Type",     value: fieldValues.phone_type          || "—" },
                { label: "Phone",          value: fieldValues.phone_number        || "—" },
                { label: "Position",       value: fieldValues.position_code       || "—" },
                { label: "Department",     value: fieldValues.department_name     || "—" },
                { label: "Job",            value: fieldValues.job_code            || "—" },
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
}
